import { motion, useReducedMotion } from 'motion/react'
import { OfficeAppIcon, type OfficeApp } from '@/components/landing/OfficeAppIcon'
import { cn } from '@/lib/utils'
import { TONES, type ListingTone } from './tones'

export interface FanDocument {
  name: string
  type: 'Word' | 'Excel' | 'PowerPoint' | 'PDF'
  description: string
}

const APP: Record<FanDocument['type'], OfficeApp | null> = {
  Word: 'word',
  Excel: 'excel',
  PowerPoint: 'powerpoint',
  PDF: null,
}

const Line = ({ w, strong }: { w: string; strong?: boolean }) => (
  <span
    className={cn('block h-[0.42em]', strong ? 'bg-[#0a0a0a]/70' : 'bg-[#0a0a0a]/[0.12]')}
    style={{ width: w }}
  />
)

/** Body of a document tile, drawn per file type. */
function DocBody({ doc, tone }: { doc: FanDocument; tone: ListingTone }) {
  const t = TONES[tone]
  if (doc.type === 'Excel') {
    return (
      <div className="mt-[1em] grid grid-cols-4 border-l border-t border-[#0a0a0a]/15 text-[0.62em] text-[#0a0a0a]/60">
        {Array.from({ length: 28 }, (_, i) => {
          const header = i < 4
          const hit = i === 9 || i === 18 || i === 23
          return (
            <span
              key={i}
              className="h-[1.9em] border-b border-r border-[#0a0a0a]/15"
              style={header ? { backgroundColor: t.bg } : hit ? { backgroundColor: '#0a0a0a', opacity: 0.08 } : undefined}
            />
          )
        })}
      </div>
    )
  }
  if (doc.type === 'PowerPoint') {
    return (
      <div className="mt-[1em] flex aspect-[4/3] flex-col justify-between bg-[#0a0a0a]/[0.04] p-[0.8em]">
        <Line w="70%" strong />
        <div className="flex h-[55%] items-end gap-[0.5em]">
          {[38, 62, 48, 84, 70].map((h, i) => (
            <span
              key={i}
              className="flex-1"
              style={{ height: `${h}%`, backgroundColor: i === 3 ? t.bg : 'rgba(10,10,10,0.18)' }}
            />
          ))}
        </div>
      </div>
    )
  }
  return (
    <div className="mt-[1em] flex flex-col gap-[0.55em]">
      <Line w="92%" />
      <Line w="100%" />
      <Line w="78%" />
      <span className="my-[0.35em] block border-l-2 py-[0.35em] pl-[0.6em]" style={{ borderColor: t.bg }}>
        <span className="flex flex-col gap-[0.45em]">
          <Line w="88%" strong />
          <Line w="64%" strong />
        </span>
      </span>
      <Line w="96%" />
      <Line w="84%" />
      <Line w="58%" />
    </div>
  )
}

/** One document mock-up: white page, tone header bar, app mark, title. */
function DocTile({ doc, tone }: { doc: FanDocument; tone: ListingTone }) {
  const app = APP[doc.type]
  const t = TONES[tone]
  return (
    <div className="flex h-full w-full flex-col bg-white p-[1.1em] text-[#0a0a0a] shadow-[0_24px_48px_-24px_rgba(10,10,10,0.45)] ring-1 ring-[#0a0a0a]/10">
      <div className="flex items-center justify-between gap-[0.6em]">
        <span className="block h-[0.5em] w-[3.2em]" style={{ backgroundColor: t.bg }} />
        {app ? (
          <OfficeAppIcon app={app} className="h-[1.9em] w-[1.9em]" />
        ) : (
          <span className="border border-[#0a0a0a]/30 px-[0.35em] text-[0.6em] font-bold">PDF</span>
        )}
      </div>
      <p className="mt-[0.9em] font-[family-name:var(--font-landing-subdisplay)] text-[1.05em] font-bold leading-[1.15] tracking-[-0.01em]">
        {doc.name}
      </p>
      <p className="mt-[0.3em] text-[0.68em] leading-[1.3] text-[#0a0a0a]/55">{doc.description}</p>
      <DocBody doc={doc} tone={tone} />
    </div>
  )
}

const POSES = [
  { rotate: -11, x: -1.2, y: 1.6 },
  { rotate: -4, x: 0, y: 0 },
  { rotate: 4, x: 0, y: 0.6 },
  { rotate: 11, x: 1.2, y: 2 },
]

/**
 * Hero visual for industry pages: three or four rotated document tiles
 * fanned out like a hand of cards. Tiles spread a little on hover (desktop).
 * Decorative: the same documents are listed, with text, further down.
 */
export function DocumentFan({ docs, tone, className }: { docs: FanDocument[]; tone: ListingTone; className?: string }) {
  const reduce = useReducedMotion()
  const fan = docs.slice(0, 4)
  const poses = fan.length === 4 ? POSES : POSES.slice(0, 3).map((p, i) => ({ ...p, rotate: [-8, 0, 8][i]! }))
  return (
    <div aria-hidden className={cn('group/fan flex justify-center', className)}>
      {fan.map((doc, i) => {
        const pose = poses[i]!
        return (
          <motion.div
            key={doc.name}
            initial={reduce ? false : { opacity: 0, y: 40, rotate: 0 }}
            animate={{ opacity: 1, y: 0, rotate: pose.rotate }}
            transition={{ duration: 0.9, delay: 0.25 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="relative shrink-0"
            style={{
              width: '14em',
              height: '18.5em',
              marginLeft: i === 0 ? 0 : '-5.5em',
              zIndex: i === 1 || i === 2 ? 2 : 1,
            }}
          >
            <div
              className="h-full w-full transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
              style={{ transform: `translate(${pose.x}em, ${pose.y}em)` }}
            >
              <div
                className="h-full w-full transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none group-hover/fan:[transform:var(--spread)]"
                style={{ ['--spread' as string]: `translateX(${(i - (fan.length - 1) / 2) * 1.6}em)` }}
              >
                <DocTile doc={doc} tone={tone} />
              </div>
            </div>
          </motion.div>
        )
      })}
    </div>
  )
}

/** Picks up to four documents with a mix of file types, Word first. */
export function pickFanDocuments<T extends FanDocument>(templates: T[]): T[] {
  const others = templates.filter((d) => d.type !== 'Word').slice(0, 2)
  const words = templates.filter((d) => d.type === 'Word').slice(0, 4 - others.length)
  const out: T[] = []
  // Interleave so non-Word tiles sit inside the fan, not at the edges.
  if (words[0]) out.push(words[0])
  if (others[0]) out.push(others[0])
  if (words[1]) out.push(words[1])
  if (others[1]) out.push(others[1])
  for (const w of words.slice(2)) if (out.length < 4) out.push(w)
  return out
}
